import * as React from 'react';

import {
  LOG_SOURCE,
  NuorderOrderingActionTypes,
  useNuorderOrdering,
} from 'root/apps/PurchaseOrder/NuorderOrdering/context/Context';
import {
  useFetchConfigs,
  useFetchItems,
  useFetchNuorderAvailability,
  useFetchNuorderBrand,
  useFetchNuorderOrder,
  useFetchNuorderProducts,
  useFetchPurchase,
  useFetchPurchaseLines,
  useFetchSortingPreference,
  useFetchVendor,
  useFetchVendorShop,
} from 'root/apps/PurchaseOrder/NuorderOrdering/context/Queries';
import {
  computeIsReadOnly,
  getItemB2bCatalogUUID,
} from 'root/apps/PurchaseOrder/NuorderOrdering/utils/dataUtils';
import { logError } from 'root/datadog/datadogLogs';

export const useDataSequencer = () => {
  const {
    purchaseID,
    state: {
      purchase,
      purchaseLines,
      items,
      vendor,
      nuorderOrder,
      nuorderProducts,
      nuorderProductAvailability,
      configs,
      sortingPreference,
    },
    dispatch,
  } = useNuorderOrdering();

  const isReadOnly = React.useMemo(() => {
    return computeIsReadOnly(purchase, nuorderOrder);
  }, [nuorderOrder, purchase]);

  const newItemIDs = React.useMemo(() => {
    if (!purchaseLines) return [];
    const itemsTmp = items || {};
    const list = purchaseLines.reduce<number[]>((acc, { itemID }) => {
      if (!itemsTmp[itemID]) {
        acc.push(itemID);
      }
      return acc;
    }, []);
    return Array.from(new Set(list)).sort();
  }, [purchaseLines, items]);

  const newUPCs = React.useMemo(() => {
    if (!items) return [];
    if (!purchase) return [];
    if (!purchase.vendorID) return [];
    const nuorderProductsTmp = nuorderProducts || {};
    const list = Object.values(items)
      .reduce<string[]>((acc, item) => {
        if (
          !getItemB2bCatalogUUID(item, purchase.vendorID) &&
          item.upc &&
          !nuorderProductsTmp[item.upc]
        ) {
          acc.push(item.upc);
        }
        return acc;
      }, [])
      .filter((upc) => upc);
    return Array.from(new Set(list)).sort();
  }, [items, nuorderProducts, purchase]);

  const newNuorderProductIDs = React.useMemo(() => {
    if (!items) return [];
    if (!purchase) return [];
    if (!purchase.vendorID) return [];
    const nuorderProductAvailabilityTmp = nuorderProductAvailability || {};
    const list = Object.values(items)
      .reduce<string[]>((acc, item) => {
        const uuid = getItemB2bCatalogUUID(item, purchase.vendorID);
        if (!nuorderProductAvailabilityTmp[uuid]) {
          acc.push(uuid);
        }
        return acc;
      }, [])
      .filter((uuid) => uuid);
    return Array.from(new Set(list)).sort();
  }, [items, nuorderProductAvailability, purchase]);

  const { error: errorConfigs } = useFetchConfigs(!configs);
  const { error: errorSortingPreference } = useFetchSortingPreference(!sortingPreference);
  const { error: errorPurchase } = useFetchPurchase(purchaseID, !!purchaseID);
  const { error: errorPurchaseLines } = useFetchPurchaseLines(purchaseID, !!purchaseID);
  const { error: errorVendor } = useFetchVendor(purchase?.vendorID || 0, !!purchase?.vendorID);
  const { error: errorVendorShop } = useFetchVendorShop(
    purchase?.vendorID || 0,
    purchase?.shopID || 0,
    !!purchase?.vendorID && !!purchase?.shopID
  );
  const { error: errorItems } = useFetchItems(newItemIDs, !!newItemIDs.length);
  const { error: errorBrand } = useFetchNuorderBrand(
    vendor?.b2bSellerUID || '',
    !!vendor?.b2bSellerUID
  );

  const { error: errorNuorderOrder, isFetching: isFetchingNuorderOrder } = useFetchNuorderOrder(
    vendor?.b2bSellerUID || '',
    purchase?.b2bOrderUID || '',
    !!vendor?.b2bSellerUID && !!purchase?.b2bOrderUID
  );
  const { error: errorNuorderProducts, isFetching: isFetchingNuorderProducts } =
    useFetchNuorderProducts(
      vendor?.b2bSellerUID || '',
      newUPCs,
      !!vendor?.b2bSellerUID && !!newUPCs.length && !isReadOnly
    );
  const { error: errorNuorderAvailability, isFetching: isFetchingNuorderAvailability } =
    useFetchNuorderAvailability(
      vendor?.b2bSellerUID || '',
      newNuorderProductIDs,
      !!vendor?.b2bSellerUID && !!newNuorderProductIDs.length && !isReadOnly
    );

  React.useEffect(() => {
    dispatch({
      type: NuorderOrderingActionTypes.SET_IS_FETCHING_ORDER,
      payload: isFetchingNuorderOrder,
    });
  }, [isFetchingNuorderOrder, dispatch]);

  React.useEffect(() => {
    dispatch({
      type: NuorderOrderingActionTypes.SET_IS_FETCHING_PRODUCTS,
      payload: isFetchingNuorderProducts,
    });
  }, [isFetchingNuorderProducts, dispatch]);

  React.useEffect(() => {
    dispatch({
      type: NuorderOrderingActionTypes.SET_IS_FETCHING_AVAILABILITY,
      payload: isFetchingNuorderAvailability,
    });
  }, [isFetchingNuorderAvailability, dispatch]);

  // Initialize items if purchaseLines exist but empty and items is not set
  React.useEffect(() => {
    if (!purchaseLines) return;
    if (items) return;
    if (purchaseLines.length === 0 && items == null) {
      dispatch({
        type: NuorderOrderingActionTypes.ADD_ITEMS,
        payload: [],
      });
    }
  }, [dispatch, purchaseLines, items]);

  const error = React.useMemo(() => {
    if (errorConfigs) return errorConfigs;
    if (errorSortingPreference) return errorSortingPreference;
    if (errorPurchase) return errorPurchase;
    if (errorPurchaseLines) return errorPurchaseLines;
    if (errorVendor) return errorVendor;
    if (errorVendorShop) return errorVendorShop;
    if (errorItems) return errorItems;
    if (errorBrand) return errorBrand;
    if (errorNuorderOrder) return errorNuorderOrder;
    if (errorNuorderProducts) return errorNuorderProducts;
    if (errorNuorderAvailability) return errorNuorderAvailability;
    return null;
  }, [
    errorBrand,
    errorConfigs,
    errorItems,
    errorNuorderAvailability,
    errorNuorderOrder,
    errorNuorderProducts,
    errorPurchase,
    errorPurchaseLines,
    errorSortingPreference,
    errorVendor,
    errorVendorShop,
  ]);

  React.useEffect(() => {
    if (error) {
      dispatch({
        type: NuorderOrderingActionTypes.SET_ALERT_MESSAGE,
        payload: {
          type: 'danger',
          title: 'Something went wrong. Try again.',
          message: 'An error occurred while fetching data.',
        },
      });
      logError(error, {
        source: LOG_SOURCE,
        file: 'useDataSequencer.ts',
      });
    } else {
      dispatch({
        type: NuorderOrderingActionTypes.SET_ALERT_MESSAGE,
        payload: null,
      });
    }
  }, [dispatch, error]);
};
